import React from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { makeStyles } from '@material-ui/core/styles';
import ForumOutlinedIcon from '@material-ui/icons/ForumOutlined';
import ScheduleIcon from '@material-ui/icons/Schedule';
import ArrowForwardIcon from '@material-ui/icons/ArrowForward';
import { tokens } from '../../theme';
import { formatDate } from '../Common/useEntries';
import { descriptionSummary } from './descriptionText';

const DAY = 86400000;

/** "Closes today", "Closes in 3 days" (last week), otherwise "Open for comment". */
export function consultationStatus(post, now = Date.now()) {
    const end = post?.endDate ? new Date(post.endDate).getTime() : NaN;
    if (Number.isNaN(end)) return { label: 'Open for comment', tone: 'open' };
    const days = Math.ceil((end - now) / DAY);
    if (days <= 1) return { label: 'Closes today', tone: 'closing' };
    if (days <= 7) return { label: `Closes in ${days} days`, tone: 'closing' };
    return { label: 'Open for comment', tone: 'open' };
}

const useStyles = makeStyles((theme) => ({
    card: {
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        overflow: 'hidden',
        backgroundColor: tokens.white,
        border: `1px solid ${tokens.border}`,
        borderRadius: tokens.radius + 4,
        color: 'inherit',
        textDecoration: 'none',
        boxShadow: tokens.shadowSm,
        transition: 'box-shadow 180ms ease, transform 180ms ease, border-color 180ms ease',
        '&:hover, &:focus-visible': {
            boxShadow: tokens.shadowLg,
            transform: 'translateY(-3px)',
            borderColor: tokens.blue100,
        },
        '&:hover $image': { transform: 'scale(1.04)' },
        '&:hover $cta, &:focus-visible $cta': { gap: 10 },
        '&:focus-visible': { outline: `3px solid ${tokens.blue500}`, outlineOffset: 2 },
        '@media (prefers-reduced-motion: reduce)': {
            transition: 'none',
            '&:hover, &:focus-visible': { transform: 'none' },
            '&:hover $image': { transform: 'none' },
        },
    },
    featured: {
        [theme.breakpoints.up('md')]: {
            flexDirection: 'row',
            '& $media': { flex: '0 0 56%', aspectRatio: 'auto', minHeight: 340 },
            '& $body': { padding: theme.spacing(4.5, 5) },
            '& $title': { fontSize: '1.75rem', WebkitLineClamp: 4 },
            '& $summary': { fontSize: '1rem', WebkitLineClamp: 5 },
        },
    },
    media: {
        position: 'relative',
        aspectRatio: '16 / 9',
        overflow: 'hidden',
        backgroundColor: tokens.blue900,
    },
    image: {
        position: 'absolute',
        inset: 0,
        width: '100%',
        height: '100%',
        objectFit: 'cover',
        transition: 'transform 400ms ease',
    },
    placeholder: {
        position: 'absolute',
        inset: 0,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        gap: theme.spacing(1.5),
        padding: theme.spacing(3),
        textAlign: 'center',
        color: tokens.goldSoft,
        backgroundImage: `radial-gradient(circle at 1px 1px, rgba(255,255,255,0.18) 1px, transparent 0),
            linear-gradient(135deg, ${tokens.blue900} 0%, ${tokens.blue700} 60%, ${tokens.blue600} 100%)`,
        backgroundSize: '22px 22px, cover',
        borderBottom: `4px solid ${tokens.gold}`,
    },
    placeholderIcon: { fontSize: 56, opacity: 0.95 },
    placeholderText: {
        fontSize: '0.75rem',
        fontWeight: 700,
        letterSpacing: '0.14em',
        textTransform: 'uppercase',
        color: '#ffffff',
        opacity: 0.85,
    },
    status: {
        position: 'absolute',
        top: theme.spacing(1.75),
        left: theme.spacing(1.75),
        display: 'inline-flex',
        alignItems: 'center',
        gap: 7,
        padding: '5px 12px',
        borderRadius: 999,
        fontSize: '0.75rem',
        fontWeight: 700,
        boxShadow: '0 2px 8px rgba(15, 23, 42, 0.25)',
    },
    open: { backgroundColor: '#ffffff', color: tokens.green },
    closing: { backgroundColor: tokens.gold, color: tokens.blue900 },
    dot: {
        width: 8,
        height: 8,
        borderRadius: '50%',
        backgroundColor: 'currentColor',
    },
    body: {
        display: 'flex',
        flexDirection: 'column',
        flexGrow: 1,
        gap: theme.spacing(1.25),
        padding: theme.spacing(3),
        minWidth: 0,
    },
    meta: {
        display: 'flex',
        flexWrap: 'wrap',
        alignItems: 'center',
        gap: theme.spacing(1),
        fontSize: '0.75rem',
        fontWeight: 700,
        letterSpacing: '0.08em',
        textTransform: 'uppercase',
        color: tokens.blue600,
    },
    metaDate: { color: tokens.muted, fontWeight: 600 },
    title: {
        margin: 0,
        fontFamily: tokens.headingFont,
        fontWeight: 700,
        fontSize: '1.1875rem',
        lineHeight: 1.35,
        color: tokens.blue900,
        display: '-webkit-box',
        WebkitLineClamp: 3,
        WebkitBoxOrient: 'vertical',
        overflow: 'hidden',
    },
    summary: {
        margin: 0,
        fontSize: '0.9375rem',
        lineHeight: 1.6,
        color: tokens.inkSoft,
        display: '-webkit-box',
        WebkitLineClamp: 3,
        WebkitBoxOrient: 'vertical',
        overflow: 'hidden',
    },
    footer: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        flexWrap: 'wrap',
        gap: theme.spacing(1.5),
        marginTop: 'auto',
        paddingTop: theme.spacing(2),
        borderTop: `1px solid ${tokens.border}`,
    },
    deadline: {
        display: 'inline-flex',
        alignItems: 'center',
        gap: 6,
        fontSize: '0.8125rem',
        color: tokens.muted,
    },
    cta: {
        display: 'inline-flex',
        alignItems: 'center',
        gap: 6,
        padding: '8px 16px',
        borderRadius: 999,
        backgroundColor: tokens.blue600,
        color: '#ffffff',
        fontSize: '0.875rem',
        fontWeight: 700,
        transition: 'gap 160ms ease',
    },
}));

/** One consultation in the listing; `featured` gives the newest one a large side-by-side layout. */
export default function ConsultationCard({ post, featured = false }) {
    const classes = useStyles();
    const status = consultationStatus(post);
    const summary = descriptionSummary(post.description, featured ? 320 : 160);

    return (
        <RouterLink
            to={`/consultationdetails/${post.slug}`}
            className={featured ? `${classes.card} ${classes.featured}` : classes.card}
        >
            <div className={classes.media}>
                {post.cover ? (
                    <img className={classes.image} src={post.cover.card} alt={post.cover.alt} loading="lazy" />
                ) : (
                    <div className={classes.placeholder} aria-hidden="true">
                        <ForumOutlinedIcon className={classes.placeholderIcon} />
                        <span className={classes.placeholderText}>{post.category || 'Public consultation'}</span>
                    </div>
                )}
                <span className={`${classes.status} ${classes[status.tone]}`}>
                    <span className={classes.dot} aria-hidden="true" />
                    {status.label}
                </span>
            </div>

            <div className={classes.body}>
                <div className={classes.meta}>
                    {post.category && <span>{post.category}</span>}
                    {post.publishedAt && <span className={classes.metaDate}>{formatDate(post.publishedAt)}</span>}
                </div>
                <h3 className={classes.title}>{post.title}</h3>
                {summary && <p className={classes.summary}>{summary}</p>}
                <div className={classes.footer}>
                    <span className={classes.deadline}>
                        <ScheduleIcon style={{ fontSize: 16 }} />
                        {post.endDate ? `Comments close ${formatDate(post.endDate)}` : 'Open until further notice'}
                    </span>
                    <span className={classes.cta}>
                        Read and comment
                        <ArrowForwardIcon style={{ fontSize: 16 }} />
                    </span>
                </div>
            </div>
        </RouterLink>
    );
}
